<template>
  <section v-if="sessionGuid && runtimeEngine === 'mastra'" class="artifact-panel"
    :class="{ 'is-chat-entry': entryMode === 'chat' }" aria-label="文档任务">
    <div v-if="entryMode !== 'chat' && actions.length" class="artifact-toolbar">
      <span>文档与表格</span>
      <el-button v-for="action in actions" :key="action.kind" size="mini" plain
        :disabled="!lastExecutionId" @click="openCreate(action.kind)">{{ action.label }}</el-button>
      <el-button size="mini" type="text" @click="loadJobs">刷新任务</el-button>
    </div>
    <p v-if="entryMode !== 'chat' && actions.length && !lastExecutionId" class="artifact-hint">先在当前会话发送一条需求消息，再选择生成或分析任务。</p>
    <div v-if="loadFailed" class="artifact-hint">
      文档任务暂时无法读取。<el-button size="mini" type="text" @click="loadJobs">重试读取</el-button>
    </div>
    <div v-for="job in (entryMode === 'chat' ? [] : jobs)" :key="job.jobId" class="artifact-job">
      <span>{{ jobLabel(job) }} · {{ statusLabel(job.status) }} · 第 {{ job.revision }} 版</span>
      <el-button v-if="job.status === 'waiting_outline_approval' || job.status === 'waiting_final_approval'"
        size="mini" type="primary" @click="openReview(job)">查看并确认</el-button>
      <el-button v-if="job.status === 'completed' && job.operationKind === 'analyze'"
        size="mini" @click="showAnalysis(job)">查看分析</el-button>
      <el-button v-if="job.status === 'completed' && job.operationKind === 'create'"
        size="mini" @click="showFiles(job)">下载成品</el-button>
      <span v-if="job.status === 'failed'" class="artifact-error">{{ job.safeErrorCode || '任务失败，请重试' }}</span>
    </div>
    <el-dialog title="创建文档任务" :visible.sync="createVisible" width="600px" append-to-body>
      <el-form label-width="88px">
        <el-form-item label="任务类型"><span>{{ actionLabel(createKind) }}</span></el-form-item>
        <el-form-item label="标题" v-if="createKind !== 'excel-analyze'">
          <el-input v-model="createTitle" maxlength="200" show-word-limit />
        </el-form-item>
        <el-form-item label="材料/内容" v-if="createKind !== 'excel-analyze'">
          <el-input v-model="createContent" type="textarea" :rows="8" maxlength="10000" show-word-limit
            :placeholder="createKind === 'excel-create' ? '请粘贴带表头的 Markdown 表格；可先让智能体生成后再确认' : '可编辑当前对话生成的草稿，确认后再制作'" />
        </el-form-item>
        <el-form-item label="原工作簿" v-if="createKind === 'excel-analyze'">
          <el-select v-model="selectedAssetId" placeholder="选择当前会话已上传的 .xlsx 附件" style="width:100%">
            <el-option v-for="asset in workbookAssets" :key="asset.assetId" :label="asset.name" :value="asset.assetId" />
          </el-select>
        </el-form-item>
        <el-form-item label="模板" v-if="createKind === 'word' || createKind === 'ppt'">
          <el-select v-model="createTemplateId" style="width:100%">
            <el-option v-for="template in templates" :key="template.value"
              :label="template.label" :value="template.value" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="busy" @click="submitCreate">创建任务</el-button>
      </span>
    </el-dialog>
    <el-dialog title="确认任务内容" :visible.sync="reviewVisible" width="600px" append-to-body>
      <p v-if="reviewJob">{{ statusLabel(reviewJob.status) }} · 请核对下面的完整内容；首次确认后开始制作预览，最终确认后才可下载成品。</p>
      <p v-if="reviewJob && reviewJob.status === 'waiting_outline_approval' && reviewJob.format === 'xlsx'"
        class="artifact-hint">请核对智能体规划的工作表、列、数据、合计公式和分组布局；批准后 Worker 将按这份结构制作。</p>
      <p v-if="reviewExcelPreview" class="artifact-hint">即将生成 {{ reviewExcelPreview.sheets.length }} 个工作表：{{ reviewExcelPreview.sheets.map(sheet => `${sheet.name}（${sheet.rows.length} 行；${sheet.columns.map(column => column.name).join('、')}）`).join('；') }}</p>
      <div v-for="sheet in reviewExcelSummary" :key="sheet.name" class="artifact-review-sheet">
        <strong>{{ sheet.name }}</strong>
        <p v-for="rule in sheet.calculations" :key="rule" class="artifact-hint">自动计算：{{ rule }}</p>
        <div class="artifact-review-scroll">
          <table class="artifact-review-table">
            <thead><tr><th v-for="(column, index) in sheet.columns" :key="index">{{ column }}</th></tr></thead>
            <tbody>
              <tr v-for="(row, rowIndex) in sheet.rows" :key="rowIndex">
                <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell === null ? '' : cell }}</td>
              </tr>
              <tr v-if="sheet.total" class="artifact-review-total">
                <td v-for="(cell, index) in sheet.total" :key="index">{{ cell === null ? '' : cell }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-if="sheet.truncated" class="artifact-hint">仅展示前 20 行；合计按全部源数据计算。</p>
      </div>
      <template v-if="reviewJob && reviewJob.status === 'waiting_final_approval'">
        <el-button size="mini" @click="showFiles(reviewJob)">预览 PDF</el-button>
        <p v-if="reviewJob.format === 'xlsx'" class="artifact-hint">Excel 预览仅展示每个工作表前 30 行样本；最终文件包含完整数据，确认前请同时核对任务内容。</p>
      </template>
      <el-input v-if="reviewJob && reviewJob.status === 'waiting_outline_approval'"
        v-model="reviewTitle" placeholder="标题" maxlength="200" />
      <el-input v-if="reviewJob && reviewJob.status === 'waiting_outline_approval'"
        v-model="reviewContent" type="textarea" :rows="8" placeholder="正文、要点或 Markdown 表格" />
      <el-input v-if="reviewJob && reviewJob.status === 'waiting_final_approval' && revising"
        v-model="reviewContent" type="textarea" :rows="8"
        :placeholder="reviewJob.format === 'xlsx' ? '直接填写修改要求，例如：最后一行显示合计，并合并合计标题单元格' : '修改后的完整内容'" />
      <p v-if="reviewJob && reviewJob.status === 'waiting_final_approval' && revising && reviewJob.format === 'xlsx'"
        class="artifact-hint">可直接说明合计行、合并单元格和删除备注等布局修改；无法安全识别的要求会提示你重新描述，不会丢失原表数据。</p>
      <div v-if="files.length" class="artifact-files">
        <a v-for="file in files" :key="file.itemId" :href="file.url"
          :download="file.role === 'main' ? file.fileName : null" target="_blank" rel="noopener noreferrer">
          {{ file.role === 'main' ? '下载' : '预览' }} {{ file.fileName }}
        </a>
      </div>
      <span slot="footer">
        <el-button @click="reviewVisible = false">关闭</el-button>
        <el-button v-if="reviewJob && reviewJob.status === 'waiting_final_approval' && !revising"
          @click="revising = true">修改后重做</el-button>
        <el-button v-if="reviewJob" type="primary" :loading="busy" @click="submitDecision">
          {{ revising ? '提交修订' : '确认并继续' }}
        </el-button>
      </span>
    </el-dialog>
    <el-dialog title="Excel 分析结果" :visible.sync="analysisVisible" width="700px" append-to-body>
      <template v-if="analysis">
        <p v-for="sheet in analysis.sheets" :key="sheet.name">
          {{ sheet.name }}：{{ sheet.dataRows }} 行数据；数值列
          {{ sheet.numericColumns.map(col => `${col.header || col.column} 合计 ${col.rawSum} (${col.source})`).join('；') || '无' }}
        </p>
        <p v-for="warning in analysis.warnings" :key="warning.code + warning.source" class="artifact-error">
          {{ warning.code }}：{{ warning.source }}
        </p>
      </template>
    </el-dialog>
    <el-dialog title="文件预览与下载" :visible.sync="filesVisible" width="600px" append-to-body>
      <div class="artifact-files">
        <a v-for="file in files" :key="file.itemId" :href="file.url"
          :download="file.role === 'main' ? file.fileName : null" target="_blank" rel="noopener noreferrer">
          {{ file.role === 'main' ? '下载' : '预览' }} {{ file.fileName }}
        </a>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import {
  createArtifactJobApi, listArtifactJobsApi, detailArtifactJobApi,
  decideArtifactJobApi, filesArtifactJobApi, analysisArtifactJobApi
} from '@/apis/agent'
import {
  availableArtifactActions, buildArtifactSpec, buildArtifactApprovalSpec, buildArtifactRevisionSpec, eligibleWorkbookAssets,
  artifactApprovalErrorMessage, isArtifactJobActive, reconcileArtifactJobList, reviewDraftFromDetail,
  summarizeExcelApprovalSpec
} from '@/utils/agent-artifact-task.mjs'

const labels = { word: '制作 Word', ppt: '制作 PPT',
  'excel-analyze': '分析 Excel', 'excel-create': '制作 Excel' }
const skillByKind = { word: 'document.word.create', ppt: 'document.ppt.create',
  'excel-analyze': 'spreadsheet.excel.process', 'excel-create': 'spreadsheet.excel.process' }
const formatByKind = { word: 'docx', ppt: 'pptx', 'excel-analyze': 'xlsx', 'excel-create': 'xlsx' }
const statusText = { waiting_outline_approval: '等待确认内容', queued: '排队中', running: '制作中',
  waiting_final_approval: '等待最终确认', finalizing: '发布中', completed: '已完成',
  failed: '失败', cancelled: '已取消' }
const requestId = () => `pc_${Date.now()}_${Math.random().toString(36).slice(2)}`

export default {
  name: 'agent-artifact-panel',
  props: {
    sessionGuid: { type: String, default: '' },
    runtimeEngine: { type: String, default: '' },
    capabilityBindings: { type: Array, default: () => [] },
    lastExecutionId: { type: String, default: '' },
    chatMessages: { type: Array, default: () => [] },
    entryMode: { type: String, default: 'manual' }
  },
  data() {
    return {
      jobs: [], createVisible: false, createKind: '', createTitle: '', createContent: '',
      createTemplateId: '', selectedAssetId: '', createRequestId: '', busy: false,
      reviewVisible: false, filesVisible: false, reviewJob: null, reviewGate: null, reviewTitle: '',
      reviewContent: '', reviewSourceContent: '', reviewDraftSpec: null,
      revising: false, decisionRequestId: '', files: [],
      analysisVisible: false, analysis: null, pollTimer: null, drafts: {}, loadFailed: false,
      artifactDisposed: false
    }
  },
  computed: {
    actions() { return availableArtifactActions(this.capabilityBindings, this.runtimeEngine) },
    workbookAssets() { return eligibleWorkbookAssets(this.chatMessages) },
    reviewExcelPreview() {
      if (this.reviewJob?.status !== 'waiting_outline_approval' || this.reviewJob.format !== 'xlsx') return null
      try { return this.reviewDraftSpec || buildArtifactSpec('excel-create', this.reviewTitle, this.reviewContent) } catch (_) { return null }
    },
    reviewExcelSummary() { return summarizeExcelApprovalSpec(this.reviewExcelPreview) },
    templates() {
      return this.createKind === 'word'
        ? [{ value: 'word-blue-classic@1', label: '蓝色经典' }, { value: 'word-minimal@1', label: '简约' }]
        : [{ value: 'ppt-blue-classic@1', label: '蓝色经典' }, { value: 'ppt-minimal@1', label: '简约' }]
    }
  },
  watch: {
    sessionGuid() { this.resetAndLoad() },
    runtimeEngine() { this.resetAndLoad() },
    createTitle() { if (this.createVisible) this.createRequestId = requestId() },
    createContent() { if (this.createVisible) this.createRequestId = requestId() },
    selectedAssetId() { if (this.createVisible) this.createRequestId = requestId() },
    createTemplateId() { if (this.createVisible) this.createRequestId = requestId() },
    reviewTitle() { if (this.reviewVisible) this.decisionRequestId = requestId() },
    reviewContent() { if (this.reviewVisible) this.decisionRequestId = requestId() }
  },
  mounted() { this.loadJobs() },
  beforeDestroy() {
    // 切换预设轮次会销毁整块任务面板；未返回的 list 不能重新启动旧轮次的轮询。
    this.artifactDisposed = true
    clearTimeout(this.pollTimer)
    this.pollTimer = null
  },
  methods: {
    actionLabel(kind) { return labels[kind] || kind },
    statusLabel(status) { return statusText[status] || status },
    jobLabel(job) {
      return job.skillCode === 'document.word.create' ? 'Word'
        : job.skillCode === 'document.ppt.create' ? 'PPT'
          : job.operationKind === 'analyze' ? 'Excel 分析' : 'Excel 制作'
    },
    resetAndLoad() {
      clearTimeout(this.pollTimer)
      this.jobs = []
      this.drafts = {}
      this.loadFailed = false
      this.reviewVisible = false
      this.loadJobs()
    },
    trackJob(job) {
      if (!job?.jobId || job.sessionGuid !== this.sessionGuid) return
      this.jobs = reconcileArtifactJobList([job], this.jobs)
      this.$emit('jobs-change', { sessionGuid: this.sessionGuid, jobs: this.jobs })
      this.loadJobs()
    },
    async loadJobs() {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
      const session = this.sessionGuid
      if (this.artifactDisposed || this.runtimeEngine !== 'mastra' || !session) return
      try {
        const response = await listArtifactJobsApi({ sessionGuid: session, limit: 20 })
        if (this.artifactDisposed || session !== this.sessionGuid || this.runtimeEngine !== 'mastra') return
        if (!response || response.code !== 0) throw new Error('任务读取失败')
        if (!Array.isArray(response.data?.list)) throw new Error('任务列表格式错误')
        this.jobs = reconcileArtifactJobList(response.data.list, this.jobs)
        this.loadFailed = false
        this.$emit('jobs-change', { sessionGuid: session, jobs: this.jobs })
      } catch (_) {
        if (this.artifactDisposed || session !== this.sessionGuid || this.runtimeEngine !== 'mastra') return
        if (!this.loadFailed && (this.actions.length || this.jobs.length || this.entryMode === 'chat')) {
          this.$message.error('文档任务读取失败，请点击重试读取')
        }
        this.loadFailed = true
      } finally {
        if (!this.artifactDisposed && this.runtimeEngine === 'mastra' && session === this.sessionGuid && this.jobs.some(job => isArtifactJobActive(job.status)
          && !['waiting_outline_approval', 'waiting_final_approval'].includes(job.status))) {
          this.pollTimer = setTimeout(() => this.loadJobs(), 3000)
        }
      }
    },
    openCreate(kind) {
      if (!this.lastExecutionId) return this.$message.warning('请先发送一条需求消息')
      const draft = [...this.chatMessages].reverse().find(message =>
        message.role === 'assistant' && typeof message.content === 'string' && message.content.trim())
      this.createKind = kind
      this.createTitle = kind === 'excel-create' ? '数据表' : '新文档'
      this.createContent = kind === 'excel-analyze' ? '' : (draft?.content || '')
      this.selectedAssetId = this.workbookAssets[0]?.assetId || ''
      this.createTemplateId = kind === 'word' ? 'word-blue-classic@1'
        : kind === 'ppt' ? 'ppt-blue-classic@1' : ''
      this.createRequestId = requestId()
      this.createVisible = true
    },
    async submitCreate() {
      if (this.busy || !this.createRequestId) return
      const kind = this.createKind
      if (kind === 'excel-analyze' && !this.selectedAssetId) {
        return this.$message.warning('请选择当前会话已上传的 Excel 工作簿')
      }
      if (kind !== 'excel-analyze') {
        try { buildArtifactSpec(kind, this.createTitle, this.createContent) } catch (_) {
          return this.$message.warning('内容不符合格式或长度要求，请核对后重试')
        }
      }
      const session = this.sessionGuid
      const request = {
        sessionGuid: session, executionId: this.lastExecutionId,
        clientRequestId: this.createRequestId, skillCode: skillByKind[kind],
        skillVersion: 'v1', operationKind: kind === 'excel-analyze' ? 'analyze' : 'create',
        format: formatByKind[kind], brief: kind === 'excel-analyze' ? '分析已选择的 Excel 工作簿'
          : this.createContent.trim(),
        inputAssetIds: kind === 'excel-analyze' ? [this.selectedAssetId] : []
      }
      const draftSpec = kind === 'excel-create'
        ? buildArtifactSpec(kind, this.createTitle, this.createContent) : null
      if (this.createTemplateId) request.templateId = this.createTemplateId
      if (draftSpec) request.draftSpec = draftSpec
      this.busy = true
      try {
        const response = await createArtifactJobApi(request)
        if (!response || response.code !== 0) throw new Error(response?.msg || '创建失败')
        if (session !== this.sessionGuid) return
        if (response.data?.jobId) {
          this.$set(this.drafts, response.data.jobId,
            { title: this.createTitle, content: this.createContent,
              ...(draftSpec ? { spec: draftSpec } : {}) })
          this.jobs = reconcileArtifactJobList([{
            jobId: response.data.jobId, sessionGuid: session,
            executionId: request.executionId,
            skillCode: request.skillCode, operationKind: request.operationKind,
            format: request.format, templateId: request.templateId || null,
            revision: response.data.revision, status: response.data.status,
            stage: response.data.stage
          }], this.jobs)
          this.$emit('jobs-change', { sessionGuid: session, jobs: this.jobs })
        }
        this.createVisible = false
        await this.loadJobs()
      } catch (error) {
        this.$message.error(error.message || '创建失败；可直接重试，不会重复建任务')
      } finally { this.busy = false }
    },
    async openReview(job) {
      try {
        const response = await detailArtifactJobApi({ sessionGuid: this.sessionGuid, jobId: job.jobId })
        if (!response || response.code !== 0 || !response.data?.gate) throw new Error('审批已过期，请刷新任务')
        this.reviewJob = response.data.job
        this.reviewGate = response.data.gate
        const draft = reviewDraftFromDetail(response.data, this.drafts[job.jobId])
        if (this.reviewJob.status === 'waiting_outline_approval' && (!draft.title || !draft.content)) {
          throw new Error('任务草稿尚未就绪，请稍后刷新任务再试')
        }
        this.reviewTitle = draft.title
        this.reviewSourceContent = draft.content
        this.reviewDraftSpec = draft.spec || null
        this.reviewContent = this.reviewJob.status === 'waiting_final_approval'
          && this.reviewJob.format === 'xlsx' ? '' : draft.content
        this.revising = false
        this.decisionRequestId = requestId()
        this.files = []
        this.reviewVisible = true
      } catch (error) { this.$message.error(error.message || '读取任务失败') }
    },
    async submitDecision() {
      if (this.busy || !this.reviewJob || !this.reviewGate) return
      const job = this.reviewJob
      const revise = this.revising
      const request = {
        sessionGuid: this.sessionGuid, jobId: job.jobId,
        gateId: this.reviewGate.gateId, revision: this.reviewGate.revision,
        requestId: this.decisionRequestId, action: revise ? 'revise' : 'approve'
      }
      if (job.status === 'waiting_outline_approval' || revise) {
        const kind = job.format === 'docx' ? 'word' : job.format === 'pptx' ? 'ppt' : 'excel-create'
        try {
          request.spec = revise && kind === 'excel-create'
            ? buildArtifactRevisionSpec(kind, this.reviewTitle, this.reviewSourceContent,
              this.reviewContent, this.reviewDraftSpec)
            : buildArtifactApprovalSpec(kind, this.reviewTitle, this.reviewSourceContent,
              this.reviewContent, this.reviewDraftSpec)
        } catch (error) {
          return this.$message.warning(kind === 'excel-create'
            ? (revise ? '暂时无法安全识别这条修改要求，请明确说明合计行、合并单元格或删除备注'
              : '结构化表格草稿已发生变化，请回到对话中说明修改要求后重新生成任务')
            : artifactApprovalErrorMessage(kind, error))
        }
      }
      this.busy = true
      try {
        const response = await decideArtifactJobApi(request)
        if (!response || response.code !== 0) throw new Error(response?.msg || '确认失败')
        this.reviewVisible = false
        await this.loadJobs()
      } catch (error) { this.$message.error(error.message || '确认失败，可重试') }
      finally { this.busy = false }
    },
    async showFiles(job) {
      try {
        const response = await filesArtifactJobApi({ sessionGuid: this.sessionGuid,
          jobId: job.jobId, revision: job.revision })
        if (!response || response.code !== 0) throw new Error('文件尚不可用')
        this.files = Array.isArray(response.data?.files) ? response.data.files : []
        if (!this.reviewVisible) this.filesVisible = true
      } catch (error) { this.$message.error(error.message || '文件读取失败') }
    },
    async showAnalysis(job) {
      try {
        const response = await analysisArtifactJobApi({ sessionGuid: this.sessionGuid,
          jobId: job.jobId, revision: job.revision })
        if (!response || response.code !== 0 || !response.data?.analysis) throw new Error('分析结果尚不可用')
        this.analysis = response.data.analysis
        this.analysisVisible = true
      } catch (error) { this.$message.error(error.message || '分析结果读取失败') }
    }
  }
}
</script>

<style scoped>
.artifact-panel { border-top: 1px solid #e5e7eb; padding: 10px 14px; background: #fff; }
.artifact-panel.is-chat-entry { border: 0; padding: 0; }
.artifact-toolbar { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.artifact-toolbar > span { font-weight: 600; margin-right: 8px; }
.artifact-job { display: flex; align-items: center; gap: 10px; padding: 6px 0; font-size: 13px; }
.artifact-hint { color: #909399; font-size: 12px; margin: 6px 0; }
.artifact-error { color: #d33; font-size: 12px; }
.artifact-files { display: flex; flex-direction: column; gap: 6px; margin-top: 12px; }
.artifact-review-sheet { margin: 10px 0; }
.artifact-review-scroll { max-height: 280px; overflow: auto; }
.artifact-review-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.artifact-review-table th, .artifact-review-table td { border: 1px solid #ddd; padding: 4px 6px; white-space: nowrap; }
.artifact-review-total { background: #edf4ff; font-weight: 600; }
</style>
